import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { matches } from "@/mocks/matches";
import { teams } from "@/mocks/teams";
import MatchCard from "@/components/feature/MatchCard";
import Breadcrumb from "@/components/feature/Breadcrumb";
import type { MatchStatus } from "@/types";
import { formatDateKey, formatDateLabel } from "@/utils/format";

type StatusFilter = "all" | MatchStatus;
type SortKey = "time-asc" | "time-desc" | "team";

const STATUS_TABS: { key: StatusFilter; label: string; icon: string }[] = [
  { key: "all", label: "全部赛事", icon: "ri-calendar-2-line" },
  { key: "live", label: "正在直播", icon: "ri-live-line" },
  { key: "upcoming", label: "即将开赛", icon: "ri-time-line" },
  { key: "replay", label: "昨日回放", icon: "ri-replay-10-line" },
];

const PAGE_SIZE = 6;

export default function Live() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [status, setStatus] = useState<StatusFilter>("all");
  const [dateKey, setDateKey] = useState("all");
  const [teamId, setTeamId] = useState("all");
  const [sortKey, setSortKey] = useState<SortKey>("time-asc");
  const [visible, setVisible] = useState(PAGE_SIZE);

  const query = searchParams.get("q") ?? "";

  const dateOptions = useMemo(() => {
    const keys = Array.from(new Set(matches.map((m) => formatDateKey(m.startTime)))).sort();
    return keys;
  }, []);

  const filtered = useMemo(() => {
    const list = matches.filter((m) => {
      if (status !== "all" && m.status !== status) return false;
      if (dateKey !== "all" && formatDateKey(m.startTime) !== dateKey) return false;
      if (teamId !== "all" && m.homeTeamId !== teamId && m.awayTeamId !== teamId) return false;
      if (query) {
        const home = teams.find((t) => t.id === m.homeTeamId);
        const away = teams.find((t) => t.id === m.awayTeamId);
        const hay = `${home?.name ?? ""}${home?.city ?? ""}${away?.name ?? ""}${away?.city ?? ""}${m.stage}`.toLowerCase();
        if (!hay.includes(query.toLowerCase())) return false;
      }
      return true;
    });
    return list.sort((a, b) => {
      if (sortKey === "time-asc") return a.startTime.localeCompare(b.startTime);
      if (sortKey === "time-desc") return b.startTime.localeCompare(a.startTime);
      const aName = teams.find((t) => t.id === a.homeTeamId)?.name ?? "";
      const bName = teams.find((t) => t.id === b.homeTeamId)?.name ?? "";
      return aName.localeCompare(bName, "zh");
    });
  }, [status, dateKey, teamId, sortKey, query]);

  useEffect(() => {
    setVisible(PAGE_SIZE);
  }, [status, dateKey, teamId, sortKey, query]);

  const resetFilters = () => {
    setStatus("all");
    setDateKey("all");
    setTeamId("all");
    setSortKey("time-asc");
    setSearchParams({});
  };

  const hasFilter = status !== "all" || dateKey !== "all" || teamId !== "all" || Boolean(query);

  return (
    <div className="px-4 md:px-8 py-6 md:py-8">
      <Breadcrumb items={[{ label: "直播赛事" }]} />

      <header className="mt-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">NBA直播赛事分类</h1>
          <p className="mt-1.5 text-sm text-foreground-500">
            浏览全部正在直播与即将开赛的NBA赛事，支持按状态、日期与球队筛选
          </p>
        </div>
        <span className="rounded-md bg-secondary-100 px-3 py-1.5 text-xs font-semibold text-secondary-900">
          共 {filtered.length} 场赛事
        </span>
      </header>

      {query && (
        <div className="mt-4 flex flex-wrap items-center gap-2 rounded-lg border border-primary-200 bg-primary-50 px-4 py-2.5 text-sm text-primary-800">
          <i className="ri-search-line" />
          搜索结果：<strong className="font-semibold">{query}</strong>
          <button
            type="button"
            onClick={() => setSearchParams({})}
            className="ml-auto inline-flex items-center gap-1 text-xs font-semibold text-primary-700 hover:text-primary-900"
          >
            <i className="ri-close-line" /> 清除搜索
          </button>
        </div>
      )}

      <div className="mt-5 flex flex-wrap gap-2">
        {STATUS_TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            onClick={() => setStatus(tab.key)}
            className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-medium whitespace-nowrap transition-colors ${
              status === tab.key
                ? "bg-primary-500 text-background-50"
                : "bg-background-100 text-foreground-600 hover:bg-background-200"
            }`}
          >
            <i className={tab.icon} />
            {tab.label}
          </button>
        ))}
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-3 rounded-lg border border-background-200 bg-background-50 p-3">
        <label className="flex items-center gap-2 text-sm text-foreground-600">
          <i className="ri-calendar-line text-primary-500" />
          <select
            value={dateKey}
            onChange={(e) => setDateKey(e.target.value)}
            className="rounded-md border border-background-300 bg-background-50 px-2.5 py-1.5 text-sm text-foreground-800 outline-none focus:border-primary-400"
          >
            <option value="all">全部日期</option>
            {dateOptions.map((key) => (
              <option key={key} value={key}>
                {formatDateLabel(`${key}T00:00:00`)}
              </option>
            ))}
          </select>
        </label>

        <label className="flex items-center gap-2 text-sm text-foreground-600">
          <i className="ri-team-line text-primary-500" />
          <select
            value={teamId}
            onChange={(e) => setTeamId(e.target.value)}
            className="rounded-md border border-background-300 bg-background-50 px-2.5 py-1.5 text-sm text-foreground-800 outline-none focus:border-primary-400"
          >
            <option value="all">全部球队</option>
            {teams.map((t) => (
              <option key={t.id} value={t.id}>
                {t.city}{t.name}
              </option>
            ))}
          </select>
        </label>

        <label className="flex items-center gap-2 text-sm text-foreground-600">
          <i className="ri-sort-desc text-primary-500" />
          <select
            value={sortKey}
            onChange={(e) => setSortKey(e.target.value as SortKey)}
            className="rounded-md border border-background-300 bg-background-50 px-2.5 py-1.5 text-sm text-foreground-800 outline-none focus:border-primary-400"
          >
            <option value="time-asc">时间升序</option>
            <option value="time-desc">时间降序</option>
            <option value="team">球队名称</option>
          </select>
        </label>

        {hasFilter && (
          <button
            type="button"
            onClick={resetFilters}
            className="ml-auto inline-flex items-center gap-1 rounded-md border border-background-300 px-3 py-1.5 text-xs font-semibold text-foreground-600 whitespace-nowrap transition-colors hover:bg-background-100"
          >
            <i className="ri-refresh-line" /> 重置筛选
          </button>
        )}
      </div>

      {filtered.length > 0 ? (
        <>
          <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {filtered.slice(0, visible).map((match) => (
              <MatchCard key={match.id} match={match} />
            ))}
          </div>
          {visible < filtered.length && (
            <div className="mt-6 flex justify-center">
              <button
                type="button"
                onClick={() => setVisible((v) => v + PAGE_SIZE)}
                className="inline-flex items-center gap-2 rounded-md border border-primary-300 bg-background-50 px-6 py-2.5 text-sm font-semibold text-primary-600 whitespace-nowrap transition-colors hover:bg-primary-50"
              >
                <i className="ri-add-line" /> 加载更多（剩余 {filtered.length - visible} 场）
              </button>
            </div>
          )}
        </>
      ) : (
        <div className="mt-8 flex flex-col items-center rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-16 text-center">
          <i className="ri-emotion-sad-line text-4xl text-foreground-300" />
          <p className="mt-3 text-sm font-medium text-foreground-700">没有找到符合条件的赛事</p>
          <p className="mt-1 text-xs text-foreground-500">试试调整筛选条件，或重置后重新浏览</p>
          <button
            type="button"
            onClick={resetFilters}
            className="mt-4 inline-flex items-center gap-1 rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-background-50 whitespace-nowrap transition-colors hover:bg-primary-600"
          >
            <i className="ri-refresh-line" /> 重置筛选条件
          </button>
        </div>
      )}
    </div>
  );
}